<template>
  <a-table
    class="table datatable thead-light dq-report-table"
    :columns="columns"
    :data-source="dataSource"
    :loading="loading"
    :pagination="pagination"
    @change="onChange"
    row-key="key"
  >
    <template #bodyCell="{ column, record }">
      <template v-if="column.key === 'Employee'">
        <div class="d-flex align-items-center">
          <div class="ms-0">
            <p class="text-dark mb-0">{{ record.employee_name }}</p>
            <span class="fs-12">
              {{ record.employee_id }}
              <span v-if="record.designation"> • {{ record.designation }}</span>
              <span v-if="record.department"> • {{ record.department }}</span>
            </span>
          </div>
        </div>
      </template>

      <template v-else-if="column.key === 'LoanDate'">
        {{ record.loan_date_label || record.loan_date }}
      </template>

      <template v-else-if="moneyColumns.includes(column.key)">
        {{ currency }}
        {{
          Number(record[column.dataIndex] || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })
        }}
      </template>

      <template v-else-if="column.key === 'Rate'">
        {{ Number(record.interest_rate || 0) }}%
      </template>

      <template v-else-if="column.key === 'Status'">
        <span class="badge" :class="statusBadge(record.status)">
          {{ record.status || "active" }}
        </span>
      </template>
    </template>
  </a-table>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  rows: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  currency: { type: String, default: "KES" },
  total: { type: Number, default: 0 },
  pageSize: { type: Number, default: 25 },
  current: { type: Number, default: 1 },
});

const emit = defineEmits(["change"]);

const columns = [
  { title: "Loan Date", dataIndex: "loan_date_label", key: "LoanDate" },
  { title: "Employee", dataIndex: "employee_name", key: "Employee" },
  { title: "Loan Type", dataIndex: "loan_type", key: "LoanType" },
  { title: "Amount", dataIndex: "amount", key: "Amount" },
  { title: "Interest", dataIndex: "interest_rate", key: "Rate" },
  { title: "Months", dataIndex: "repayment_months", key: "Months" },
  { title: "Installment", dataIndex: "installment", key: "Installment" },
  { title: "Method", dataIndex: "payment_method", key: "Method" },
  { title: "Balance", dataIndex: "balance", key: "Balance" },
  { title: "Status", dataIndex: "status", key: "Status" },
];

const moneyColumns = ["Amount", "Installment", "Balance"];

const dataSource = computed(() =>
  (props.rows || []).map((r, idx) => ({
    key: r.id || idx,
    loan_date: r.loan_date,
    loan_date_label: r.loan_date_label,
    employee_id: r.employee_id,
    employee_name: r.employee_name,
    department: r.department,
    designation: r.designation,
    loan_type: r.loan_type,
    amount: r.amount,
    interest_rate: r.interest_rate,
    repayment_months: r.repayment_months,
    installment: r.installment,
    payment_method: r.payment_method,
    balance: r.balance,
    status: r.status,
  })),
);

const pagination = computed(() => ({
  total: props.total,
  pageSize: props.pageSize,
  current: props.current,
  showTotal: (t) => `Total ${t} items`,
}));

function onChange(pagination, filters, sorter) {
  emit("change", pagination, filters, sorter);
}

function statusBadge(status) {
  if (status === "rejected" || status === "closed")
    return "bg-transparent-danger text-danger border border-danger";
  if (status === "pending")
    return "bg-transparent-warning text-warning border border-warning";
  return "bg-transparent-success text-success border border-success";
}
</script>

<style scoped>
@media (max-width: 767.98px) {
  :deep(.dq-report-table .ant-table) {
    min-width: 980px;
  }

  :deep(.dq-report-table .ant-table-cell) {
    vertical-align: top;
    white-space: normal;
  }
}
</style>
